import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, finalize, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { TokenStorageService } from './token-storage.service';

/** Answer of login, set-password and refresh in the partner area. */
export interface PartnerSession {
  access_token: string;
  token_type: string;
  expires_in: number;
  /** Null until the profile step after sign-up. */
  name: string | null;
  profile_completed: boolean;
  /** A Google sign-up just created the partner. */
  is_new_account?: boolean;
}

/** Sign-up like a shop's: e-mail and password, login once the address is confirmed. */
export interface PartnerRegistration {
  email: string;
  password: string;
  accept_terms: boolean;
}

export type PartnerClientStatus = 'registered' | 'onboarding' | 'live';

/**
 * A shop the partner brought in: through its link, or created for a client. Only
 * while the agency works in the account (`managed`) does it see its id and address.
 */
export interface PartnerClient {
  name: string | null;
  registered_at: string;
  status: PartnerClientStatus;
  managed: boolean;
  public_id: string | null;
  email: string | null;
  created_account: boolean;
  awaits_claim: boolean;
  sales_last_30_days?: number;
  vouchers_last_30_days?: number;
}

export interface PartnerOverview {
  name: string | null;
  /** False until the partner gave its name after signing up. */
  profile_completed: boolean;
  email: string;
  code: string;
  link: string;
  clients: PartnerClient[];
}

/** A shop account the agency creates for a client; it accepts the AGB on its behalf. */
export interface PartnerClientCreate {
  email: string;
  name: string;
  accept_terms_on_behalf: boolean;
}

/**
 * A company session for working in a client's account: a 30-minute company token
 * naming the agency. There's no refresh cookie for it; a new one comes from
 * openClient() with the partner session (see AuthService.refreshToken).
 */
export interface ActingSession {
  access_token: string;
  token_type: string;
  expires_in: number;
  public_company_id: string;
  company_name: string;
  company_stripe_account_id: string | null;
  company_is_stripe_onboarding_success: boolean;
  company_is_email_verified: boolean;
  company_is_profile_completed: boolean;
  acting_partner_name: string;
}

/** Same message whether or not the address is known: the page never learns who is. */
export interface PartnerMessage {
  message: string;
}

/**
 * The partner area's session and API. Partner requests (`/partner/...`) get the
 * partner token from partnerAuthInterceptor; the company session is untouched, so an
 * agency can work in a client's account and return without logging in again.
 */
@Injectable({ providedIn: 'root' })
export class PartnerService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly apiUrl = environment.apiUrl;

  /** Bumped on login/logout so templates can react (localStorage isn't a signal). */
  private readonly sessionTick = signal(0);
  readonly isLoggedIn = computed(() => {
    this.sessionTick();
    return !!this.tokenStorage.getPartnerAccessToken();
  });
  readonly name = computed(() => {
    this.sessionTick();
    return this.tokenStorage.getPartnerName();
  });

  // -- sign-up ------------------------------------------------------------

  /** Mails a confirmation link; the partner logs in once it opened it. */
  register(data: PartnerRegistration): Observable<PartnerMessage> {
    return this.http.post<PartnerMessage>(`${this.apiUrl}/partner/auth/register`, data);
  }

  verifyEmail(token: string): Observable<PartnerMessage> {
    return this.http.post<PartnerMessage>(`${this.apiUrl}/partner/auth/verify-email`, { token });
  }

  resendVerification(email: string): Observable<PartnerMessage> {
    return this.http.post<PartnerMessage>(`${this.apiUrl}/partner/auth/resend-verification`, {
      email,
    });
  }

  /**
   * Google for the partner area (not the shop login, even for the same Google
   * account). 'register' may create the partner and accepts the Teilnahmebedingungen.
   */
  loginWithGoogle(credential: string, mode: 'login' | 'register'): Observable<PartnerSession> {
    return this.http
      .post<PartnerSession>(
        `${this.apiUrl}/partner/auth/google`,
        { credential, mode, accept_terms: mode === 'register' },
        { withCredentials: true },
      )
      .pipe(tap((session) => this.store(session)));
  }

  /** The name after sign-up; the first time, the link also comes by mail. */
  saveProfile(name: string): Observable<PartnerMessage> {
    return this.http.put<PartnerMessage>(`${this.apiUrl}/partner/profile`, { name }).pipe(
      tap(() => {
        this.tokenStorage.setPartnerName(name);
        this.sessionTick.update((n) => n + 1);
      }),
    );
  }

  // -- session ------------------------------------------------------------

  login(email: string, password: string): Observable<PartnerSession> {
    return this.http
      .post<PartnerSession>(
        `${this.apiUrl}/partner/auth/login`,
        { email, password },
        { withCredentials: true },
      )
      .pipe(tap((session) => this.store(session)));
  }

  /** Mails a link that sets (or resets) the password. */
  requestPasswordLink(email: string): Observable<PartnerMessage> {
    return this.http.post<PartnerMessage>(`${this.apiUrl}/partner/auth/password-link`, { email });
  }

  setPassword(token: string, password: string): Observable<PartnerSession> {
    return this.http
      .post<PartnerSession>(
        `${this.apiUrl}/partner/auth/set-password`,
        { token, password },
        { withCredentials: true },
      )
      .pipe(tap((session) => this.store(session)));
  }

  /** New partner token from the refresh cookie (rotates it). */
  refresh(): Observable<PartnerSession> {
    return this.http
      .post<PartnerSession>(`${this.apiUrl}/partner/auth/refresh`, {}, { withCredentials: true })
      .pipe(tap((session) => this.store(session)));
  }

  logout(): void {
    this.http
      .post(`${this.apiUrl}/partner/auth/logout`, {}, { withCredentials: true })
      .pipe(
        finalize(() => {
          this.clear();
          this.router.navigate(['/partner/login']);
        }),
      )
      .subscribe({ error: () => undefined });
  }

  /** Drops the local partner session without asking the backend. */
  clear(): void {
    this.tokenStorage.clearPartner();
    this.sessionTick.update((n) => n + 1);
  }

  private store(session: PartnerSession): void {
    this.tokenStorage.setPartnerSession(session.access_token, session.name ?? '');
    this.sessionTick.update((n) => n + 1);
  }

  // -- partner area -------------------------------------------------------

  overview(): Observable<PartnerOverview> {
    return this.http.get<PartnerOverview>(`${this.apiUrl}/partner/overview`);
  }

  createClient(data: PartnerClientCreate): Observable<PartnerClient> {
    return this.http.post<PartnerClient>(`${this.apiUrl}/partner/clients`, data);
  }

  /** A company session for the client's account (see AuthService.startActingSession). */
  openClient(publicCompanyId: string): Observable<ActingSession> {
    return this.http.post<ActingSession>(
      `${this.apiUrl}/partner/clients/${encodeURIComponent(publicCompanyId)}/session`,
      {},
    );
  }

  /**
   * Asks the owner of an existing shop for access. The answer is the same whether or
   * not the address has an account; an approved shop shows up in the list.
   */
  requestAccess(email: string): Observable<PartnerMessage> {
    return this.http.post<PartnerMessage>(`${this.apiUrl}/partner/access-requests`, { email });
  }

  /** Ends the agency's own access to a client's account. */
  leaveClient(publicCompanyId: string): Observable<PartnerMessage> {
    return this.http.post<PartnerMessage>(
      `${this.apiUrl}/partner/clients/${encodeURIComponent(publicCompanyId)}/leave`,
      {},
    );
  }

  /** Mails the claim link again, to a corrected address if given. */
  resendClaimMail(publicCompanyId: string, email: string | null): Observable<PartnerClient> {
    return this.http.post<PartnerClient>(
      `${this.apiUrl}/partner/clients/${encodeURIComponent(publicCompanyId)}/claim-mail`,
      { email },
    );
  }
}
